import { PayloadAction } from "@reduxjs/toolkit";
import { QueryParams } from "@router/route-params";
import {
  AppState,
  setIsBetaTestingEnabled,
  setIsDevModeEnabled,
} from "@store/app/app-slice";
import { DevFeaturesState } from "@store/dev-features/dev-features-slice";

/** Allowed dev tool keys */
export type DevToolKey =
  | "showAllPlans"
  | "showAllProducts"
  | "showFullDateTime"
  | "showHardwareLicense"
  | "allowBulkOperations"
  | "enableSentry";

type DevFeatureBase = {
  /** Display name to show in the table */
  label: string;

  /** A description of what this feature or tool does */
  description: string;
};

export type SetEnabledAction = (isEnabled: boolean) => PayloadAction<boolean>;

type Feature = DevFeatureBase & {
  /** query param to enable/disable this feature */
  queryParam: QueryParams;

  /** Key name of the variable for this feature in the app state */
  appStateKey: keyof AppState;

  /** Redux action to update the feature's state */
  setEnabledAction: SetEnabledAction;
};

/** Type definition for the callback of toggle features or toggle tools */
export type TogglesCallback = (isEnabled: boolean) => void;

/** Type definition for the callback of button dev tools */
export type ButtonsCallback = () => void;

type DevTool = DevFeatureBase & {
  /** The action this tool performs (maps to a hook function) */
  action: DevToolKey;

  /** Button text */
  buttonText?: string;

  /** Control type for the dev tool, defaults to button */
  controlType: "button" | "toggle";

  /**
   * Key name of the variable for this feature
   * in the dev features state (for toggle controls)
   */
  featureStateKey?: keyof DevFeaturesState;
};

export interface FeatureToggle extends Omit<Feature, "setEnabledAction"> {
  /** Whether the feature is currently enabled */
  isEnabled: boolean;

  /** Callback when the feature is toggled */
  onToggle: TogglesCallback;
}

export interface DevToolItem extends Omit<DevTool, "action"> {
  /** Callback function to execute when the tool is used */
  onClick?: ButtonsCallback;

  /** Callback function to execute when the tool is toggled (for toggle controls) */
  onToggle?: TogglesCallback;

  /** Whether the tool is currently enabled (for toggle controls) */
  isEnabled?: boolean;
}

/** Union type for items in the features table */
export type TableItem = FeatureToggle | DevToolItem;

/** Headers for the features table */
export const TABLE_HEADERS = ["Name", "Description", ""];

/**
 * Feature configuration object for development features
 */
export const FEATURES: Feature[] = [
  {
    label: "Dev Mode",
    description:
      "Enables features for developers and internal users that won't be released to customers",
    queryParam: QueryParams.enableDev,
    appStateKey: "isDevModeEnabled",
    setEnabledAction: setIsDevModeEnabled,
  },
  {
    label: "Beta Mode",
    description:
      "Enables features that are mostly complete but not yet ready for release to customers.",
    queryParam: QueryParams.enableBeta,
    appStateKey: "isBetaTestingEnabled",
    setEnabledAction: setIsBetaTestingEnabled,
  },
];

export const DEV_TOOLS: DevTool[] = [
  {
    label: "Show all plans",
    description:
      "Shows all plans in a workspace, including the technical ones like 'Default Plan - Minimal'",
    action: "showAllPlans",
    featureStateKey: "shouldShowAllPlans",
    controlType: "toggle",
  },
  {
    label: "Show all products",
    description: "Shows all products during plan creation",
    action: "showAllProducts",
    featureStateKey: "shouldShowAllProducts",
    controlType: "toggle",
  },
  {
    label: "Show full date and time",
    description:
      "Shows the full date and time letting you see or select specific hours and minutes",
    action: "showFullDateTime",
    featureStateKey: "shouldShowFullDateTime",
    controlType: "toggle",
  },
  {
    label: "Show hardware license",
    description:
      "Shows hardware licenses such as table and plan creation options",
    action: "showHardwareLicense",
    featureStateKey: "shouldShowHardwareLicense",
    controlType: "toggle",
  },
  {
    label: "Allow bulk operations",
    description:
      "Shows a checkbox on each row of the plans table to deactivate or delete the selected plans at once",
    action: "allowBulkOperations",
    featureStateKey: "shouldAllowBulkOperations",
    controlType: "toggle",
  },
  {
    label: "Enable Sentry",
    description:
      "Allows sending errors to Sentry from numbered environments or localhost. By default, errors are only sent from production environments.",
    action: "enableSentry",
    featureStateKey: "isSentryEnabled",
    controlType: "toggle",
  },
];
